/**
 * AI Utilities
 *
 * Production note: Claude requests must be routed via Supabase Edge Functions.
 * This module intentionally contains no direct API key usage.
 */

/**
 * Convert File to base64 string for Claude Vision API
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64Data = base64.split(',')[1] || base64;
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Get media type from file
 */
const getMediaType = (file: File): string => {
  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();

  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'application/pdf';

  // Map common types to Claude-supported formats
  if (type.startsWith('image/')) {
    if (type === 'image/jpeg' || type === 'image/jpg') return 'image/jpeg';
    if (type === 'image/png') return 'image/png';
    if (type === 'image/gif') return 'image/gif';
    if (type === 'image/webp') return 'image/webp';
  }

  // Default to jpeg for unknown image types
  return 'image/jpeg';
};

/**
 * Retry logic with exponential backoff for rate limits
 */
const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Check if it's a rate limit error
      if (error?.status === 429 || error?.error?.type === 'rate_limit_error') {
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(`Rate limited. Retrying in ${delay}ms... (Attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // If it's not a rate limit error, throw immediately
      throw error;
    }
  }

  throw lastError || new Error('Max retries exceeded');
};

export { fileToBase64, getMediaType, withRetry };
